/* eslint-disable no-useless-computed-key */
import React, { useContext, useEffect, useState } from 'react';

import { ThemeContext } from '@Hub/Theme';

import useMediaQuery from '@core/hooks/useMediaQuery';
import useUniqueId from '@core/hooks/useUniqueId';
import classy from '@core/utils/classy';

import Button from '@ui/Button';
import Dropdown from '@ui/Dropdown';
import Menu, { MenuHeader } from '@ui/Menu';
import MenuItem from '@ui/Menu/Item';

import classes from '../../style.module.scss';
import { useDarkMode } from '../../utils/hooks';

interface ThemeOverrideProps {
  setTheme: (theme: string) => void;
}

const ThemeOverride = ({ setTheme }: ThemeOverrideProps) => {
  const colorScheme = useDarkMode();
  const { colorMode, setColorMode } = useContext(ThemeContext);
  const [themeOverride, setThemeOverride] = useState(colorScheme);
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  const [visible, setVisible] = useState(false);
  const uid = useUniqueId('ThemeToggle');

  // set theme when toggled
  useEffect(() => {
    // except…when there’s a preview dark mode query param
    const params = new URLSearchParams(window.location.search);
    const previewDarkMode = params.get('previewDarkMode');

    // hide until loaded
    if (colorMode === themeOverride) setVisible(true);

    if (!previewDarkMode) {
      const colorModeElement = document.querySelector('[data-color-mode]');
      if (colorModeElement) {
        colorModeElement.setAttribute('data-color-mode', themeOverride);
      }
    }
    localStorage.setItem('color-scheme', themeOverride);
    if (setColorMode) {
      setColorMode(themeOverride);
    }
    setTheme(themeOverride); // for header RDMD blocks (Owlbot)
  }, [colorMode, setColorMode, setTheme, themeOverride]);

  // set theme
  const toggleTheme = theme => setThemeOverride(theme);

  return (
    <Dropdown className={classes['ThemeToggle-wrapper']} trigger="contextMenu">
      <Button
        aria-label={`Toggle to ${themeOverride === 'light' ? 'dark' : 'light'} mode`}
        bem={{ ['slate_text']: true }}
        className={`rm-ThemeToggle ${classes.ThemeToggle} ${visible ? classes.ThemeToggle_active : ''}`}
        id={uid('button')}
        onClick={() => toggleTheme(themeOverride === 'light' ? 'dark' : 'light')}
        size="sm"
      >
        <svg
          aria-labelledby={uid('button')}
          className={classy(
            'Icon',
            classes['ThemeToggle-Icon'],
            (colorMode === 'dark' || (colorMode === 'system' && prefersDarkMode)) && classes['ThemeToggle-Icon_active'],
          )}
          role="img"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <title>Moon (Dark Mode)</title>
          <path d="M19 14.79C18.8427 16.4922 18.2039 18.1144 17.1582 19.4668C16.1126 20.8192 14.7035 21.8458 13.0957 22.4265C11.4879 23.0073 9.74798 23.1181 8.0795 22.7461C6.41102 22.3741 4.88299 21.5345 3.67423 20.3258C2.46546 19.117 1.62594 17.589 1.25391 15.9205C0.881876 14.252 0.992717 12.5121 1.57346 10.9043C2.1542 9.29651 3.18083 7.88737 4.53321 6.84175C5.8856 5.79614 7.5078 5.15731 9.21 5C8.21341 6.34827 7.73385 8.00945 7.85853 9.68141C7.98322 11.3534 8.70386 12.9251 9.8894 14.1106C11.0749 15.2961 12.6466 16.0168 14.3186 16.1415C15.9906 16.2662 17.6517 15.7866 19 14.79Z" />
          <path
            className={classes['ThemeToggle-Icon-star']}
            d="M18.3707 1C18.3707 3.22825 16.2282 5.37069 14 5.37069C16.2282 5.37069 18.3707 7.51313 18.3707 9.74138C18.3707 7.51313 20.5132 5.37069 22.7414 5.37069C20.5132 5.37069 18.3707 3.22825 18.3707 1Z"
          />
        </svg>

        <svg
          aria-labelledby={uid('button')}
          className={classy(
            'Icon',
            classes['ThemeToggle-Icon'],
            (colorMode === 'light' || (colorMode === 'system' && !prefersDarkMode)) &&
              classes['ThemeToggle-Icon_active'],
          )}
          role="img"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <title>Sun (Light Mode)</title>
          <path className={classes['ThemeToggle-Icon-ray']} d="M12 1V3" />
          <path className={classes['ThemeToggle-Icon-ray']} d="M18.36 5.64L19.78 4.22" />
          <path className={classes['ThemeToggle-Icon-ray']} d="M21 12H23" />
          <path className={classes['ThemeToggle-Icon-ray']} d="M18.36 18.36L19.78 19.78" />
          <path className={classes['ThemeToggle-Icon-ray']} d="M12 21V23" />
          <path className={classes['ThemeToggle-Icon-ray']} d="M4.22 19.78L5.64 18.36" />
          <path className={classes['ThemeToggle-Icon-ray']} d="M1 12H3" />
          <path className={classes['ThemeToggle-Icon-ray']} d="M4.22 4.22L5.64 5.64" />
          <path d="M12 17C14.7614 17 17 14.7614 17 12C17 9.23858 14.7614 7 12 7C9.23858 7 7 9.23858 7 12C7 14.7614 9.23858 17 12 17Z" />
        </svg>
      </Button>
      <Menu>
        <MenuHeader>Color Scheme</MenuHeader>
        <MenuItem active={themeOverride === 'system'} icon="icon icon-monitor" onClick={() => toggleTheme('system')}>
          Same as System
        </MenuItem>
        <MenuItem active={themeOverride === 'light'} icon="icon icon-sun" onClick={() => toggleTheme('light')}>
          Light
        </MenuItem>
        <MenuItem active={themeOverride === 'dark'} icon="icon icon-moon" onClick={() => toggleTheme('dark')}>
          Dark
        </MenuItem>
      </Menu>
    </Dropdown>
  );
};

export default ThemeOverride;
